import { FC, type JSX } from 'react';

import { productUnits } from '@eneco/enumerations/productUnits';
import { typedYupResolver } from '@eneco/hooks/form';
import { Box, Button, Form, InputText, Stack, Text } from '@eneco/sparky';
import type { SmeSolarPanelsStepRendering } from '@sitecore/types/SmeSolarPanelsStep';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';


import { SOLAR_PANEL_OUTPUT } from '../../helpers';

interface FormFields {
  solarPanelsQuantity: string;
}

interface Props {
  textualData?: SmeSolarPanelsStepRendering['fields'];
  handleAction: (output: number) => void;
  handleCancel: () => void;
  shouldSubmit?: boolean;
}

export const SolarPanelsOutputCalculator: FC<Props> = ({
  textualData,
  handleAction,
  handleCancel,
  shouldSubmit = true,
}): JSX.Element => {
  const formSchema = yup.object({
    solarPanelsQuantity: yup
      .number()
      .integer(textualData?.redeliveryEstimation?.solarPanelsQuantityFormField?.value?.validationMessage)
      .typeError(textualData?.redeliveryEstimation?.solarPanelsQuantityFormField?.value?.validationMessage ?? '')
      .required(textualData?.redeliveryEstimation?.solarPanelsQuantityFormField?.value?.requiredMessage)
      .min(1, textualData?.redeliveryEstimation?.solarPanelsQuantityFormField?.value?.validationMinValueMessage)
      .max(99, textualData?.redeliveryEstimation?.solarPanelsQuantityFormField?.value?.validationMaxValueMessage),
  });

  const {
    handleSubmit,
    register,
    watch,
    trigger,
    formState: { errors, isValid },
  } = useForm<FormFields>({
    mode: 'onChange',
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  const solarPanelsQuantity = watch('solarPanelsQuantity');

  const submit = () => {
    if (!isValid && !shouldSubmit) {
      trigger();
      return;
    }
    handleAction(Number(solarPanelsQuantity) * SOLAR_PANEL_OUTPUT);
  };

  return (
    <Form onSubmit={handleSubmit(submit)}>
      <Stack direction="column" gap="10">
        <Stack.Item>
          <InputText
            {...register('solarPanelsQuantity')}
            label={textualData?.redeliveryEstimation?.solarPanelsQuantityFormField?.value?.label ?? ''}
            placeholder={textualData?.redeliveryEstimation?.solarPanelsQuantityFormField?.value?.placeholder}
            hint={textualData?.redeliveryEstimation?.solarPanelsQuantityFormField?.value?.hint}
            error={errors.solarPanelsQuantity?.message}
          />

          <Box paddingTop="6">
            <Stack direction="column">
              <Text>{textualData?.redeliveryEstimation?.estimateText?.value}</Text>
              <Text size="BodyXL" weight="bold">
                {isValid ? Number(solarPanelsQuantity) * SOLAR_PANEL_OUTPUT : 0} {productUnits.electricity}
              </Text>
            </Stack>
          </Box>
        </Stack.Item>
        <Stack direction={{ initial: 'column', md: 'row' }} gap="2">
          <Button type={shouldSubmit ? 'submit' : 'button'} onClick={submit}>
            {textualData?.redeliveryEstimation?.solarPanelsRedeliveryEstimationDialog?.value?.submitButtonText}
          </Button>
          <Button action="secondary" onClick={handleCancel}>
            {textualData?.redeliveryEstimation?.solarPanelsRedeliveryEstimationDialog?.value?.cancelButtonText}
          </Button>
        </Stack>
      </Stack>
    </Form>
  );
};
